import { css } from "@emotion/css";
import styled from "@emotion/styled";
import {
  Badge,
  Button,
  Flex,
  Pagination,
  useMantineColorScheme
} from "@mantine/core";
import { DefaultApiRoutingsRoutingIdDeleteRequest, ItemsGet200ResponseRowsInnerRoutingsInner } from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconSearch, IconTrash } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import type { Column, SortColumn } from "react-data-grid";
import DataGrid, { SelectColumn } from "react-data-grid";

import { useModal } from "@/features/modal/ModalStackManager";
import { useSub } from "@/hooks";

import { mutateRoutings, routings } from "@/api/routing/useRoutingQuery";
import { routingsHeader } from "@/constants/columnHeader";
import { queryDefaultValues } from "@/constants/queryDefaultValues";
import { consumeTypeColor, routingTypeColor } from "@/constants/routings";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { MoldDetailForm } from "@/features/mold/form/moldDetailForm";
import { OperationDetailForm } from "@/features/operations/components/form/operationDetailForm";
import { RoutingEditForm } from "@/features/routing/components/form/RoutingUpdateForm";
import { CustomFilter } from "@/features/ui/Base/List/CustomFilter/CustomFilter";
import { CustomSorter } from "@/features/ui/Base/List/CustomSorter/CustomSorter";
import { SearchBox, SearchProps } from "@/features/ui/Base/List/SearchBox/SearchBox";
import customAlert from "@/features/ui/alert/alert";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import useQueryString from "@/hooks/useQueryString";
import useUpdateUrlParams from "@/hooks/useUpdateUrlParams";
import { theme } from "@/styles/theme";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { RoutingDetailForm } from "../form/RoutingDetailForm";

interface ColorThemeProps {
  isDarkMode: boolean;
}

export const RoutingTable = () => {
  const [initialQueryString, setQueryString] = useQueryString(queryDefaultValues);

  const { t } = useTranslation();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const isDarkMode: boolean = colorScheme === "light";

  const [sortColumns, setSortColumns] = useState<readonly SortColumn[]>([]);

  const [selectedRows, setSelectedRows] = useState(
    (): ReadonlySet<any> => new Set()
  );
  const [activePage, setPage] = useState(initialQueryString.page);
  const [query, setQuery] = useState(JSON.parse(initialQueryString.query));
  const [sort, setSort] = useState(initialQueryString.sort);
  const [search, setSearch] = useState<SearchProps>({
    search: initialQueryString.search,
    searchFields: initialQueryString.searchFields,
    pageSize: initialQueryString.pageSize,
  });

  const updateUrlParams = useUpdateUrlParams();

  const { openModal } = useModal();

  const { data: routing, refetch } = useQuery(routings.get({
    query: query,
    search: search.search,
    searchFields: search.searchFields.length
      ? search.searchFields
      : routingsHeader.map((routing) => routing.value),
    page: activePage,
    pageSize: Number(search.pageSize),
    sort: sort.length ? sort.join(",") : "-id",
    populate: ["item", "operation", "mold"],
  }));

  // 새로운 데이터가 로드될 때, 현재 페이지가 유효하지 않으면 1페이지로 설정
  useEffect(() => {
    if (routing && activePage > (routing?.data?.totalPages ?? 0)) {
      setPage(1);
      setQueryString((prev) => ({
        ...prev,
        page: 1,
      }));
    }
  }, [routing, activePage, setQueryString]);

  // 페이지 Query String으로 공정 이동
  useEffect(() => {
    updateUrlParams({
      page: activePage,
      pageSize: search.pageSize,
      search: search.search,
      searchFields: search.searchFields,
      query: JSON.stringify(query),
      sort: sort
    }, queryDefaultValues);
  }, [activePage, search, query, sort, updateUrlParams]);

  const rows: readonly ItemsGet200ResponseRowsInnerRoutingsInner[] =
    routing?.data?.rows ?? [];
  const selectedRowsRef = useRef<ReadonlySet<any>>(new Set());

  const { mutate: deleteMutate } = useMutation(
    (params: DefaultApiRoutingsRoutingIdDeleteRequest) =>
      mutateRoutings.delete(params).mutationFn(params as DefaultApiRoutingsRoutingIdDeleteRequest | any),
    {
      onSuccess: () => {
        refetch();
      },
    }
  );

  const columns: readonly Column<ItemsGet200ResponseRowsInnerRoutingsInner>[] = [
    {
      ...SelectColumn,
      width: 70,
      maxWidth: 500,
      resizable: true,
      headerCellClass: css`
        & > * {
          justify-content: flex-start;
          padding-left: 24px;
        }
      `,
      cellClass: css`
        .rdg-checkbox-label {
          padding-left: 24px;
        }
      `,
    },
    {
      key: "itemName",
      name: "품목명",
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        const detailItemAction = () => {
          openModal(
            <ItemsDetailForm itemCode={row.itemCode ?? ""} />,
            null,
            "재고 상세"
          );
        };
        return <DetailLink onClick={detailItemAction}>{row?.item?.name ?? '-'}</DetailLink>;
      },
    },
    {
      key: "itemCode",
      name: "품목코드",
      sortable: true,
      resizable: true,
      // formatter: ({ row }) => {
      //   const detailItemAction = () => {
      //     openModal(
      //       <ItemsDetailForm itemCode={row.itemCode ?? ""} />,
      //       null,
      //       "재고 상세"
      //     );
      //   };
      //   return <Anchor onClick={detailItemAction}>{row.itemCode}</Anchor>;
      // },
    },
    {
      key: "spec",
      name: "규격",
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return <>{row.item?.spec}</>;
      },
    },
    {
      key: "operationCode",
      name: "공정코드",
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <DetailLink onClick={() => openModal(<OperationDetailForm operationCode={row.operationCode} />, null, "")}>
            {row.operationCode}
          </DetailLink>
        );
      },
    },
    {
      key: "code",
      name: "라우팅코드",
      sortable: true,
      resizable: true,
    },
    {
      key: "routingType",
      name: "라우팅유형",
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <Badge size="lg" color={routingTypeColor[row.routingType!]}>{t(row.routingType!)}</Badge>
        );

      }
    },
    {
      key: "moldCode",
      name: "금형코드",
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <DetailLink
            onClick={() => openModal(<MoldDetailForm moldCode={row.moldCode} />, null, "")}
          >
            {row.moldCode}
          </DetailLink>
        )
      },
    },
    {
      key: "consumeType",
      name: "소모 시점",
      sortable: true,
      resizable: true,
      formatter: ({ row }) => <Badge size="lg" color={consumeTypeColor[row.consumeType!]}>{t(row.consumeType!)}</Badge>,
    },
    {
      key: "seq",
      name: "라우팅순서",
      sortable: true,
      resizable: true,
      cellClass: css`
        justify-content: right;
      `,
    },
    {
      key: "options",
      name: "액션",
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        const deleteItemAction = async (tid: number) => {
          if (window.confirm("정말로 삭제하시겠습니까?")) {
            deleteItem(tid);
          }
        };

        // delete item after confirm
        const deleteItem = async (deleteItemId: number) => {
          deleteMutate({ routingId: deleteItemId }, {
            onSuccess: (res) => {
              if (res.status === 200) {
                customAlert(
                  "라우팅 삭제에 성공하였습니다.",
                  "삭제 성공",
                  "green"
                );
              } else {
                customAlert("삭제가 불가능한 라우팅입니다.", "삭제 실패", "red");
              }
            },
          });
        };

        const detailItemAction = () => {
          openModal(
            <RoutingDetailForm routingCode={formatterProps?.row.code} />,
            null,
            "라우팅 상세정보"
          );
        };

        return (
          <OptionBox>
            <OptionBtn
              variant="subtle"
              onClick={detailItemAction}
              leftIcon={<IconSearch />}
              color="blue"
            ></OptionBtn>
            <OptionBtn
              variant="subtle"
              leftIcon={<IconEdit />}
              color="yellow"
              onClick={() => {
                openModal(
                  <RoutingEditForm formatterProps={formatterProps?.row} />,
                  null,
                  "라우팅 수정",
                  true
                );
              }}
            ></OptionBtn>
            <OptionBtn
              variant="subtle"
              leftIcon={<IconTrash />}
              color="red"
              onClick={() => {
                deleteItemAction(formatterProps?.row?.id as any);
              }}
            ></OptionBtn>
          </OptionBox>
        );
      },
    },
  ];

  useSub("deleteSelectedItems", () => {
    const rows = Array.from(selectedRowsRef.current);
    if (!rows.length) return;
    const deleteRows: number[] = Array.from(rows[rows.length - 1].values());
    if (window.confirm("정말로 삭제하시겠습니까?")) {
      deleteRows.forEach((row: number) => {
        deleteMutate({ routingId: row });
      });
      selectedRowsRef.current = new Set();
    }
  });

  return (
    <RoutingTableWrapper>
      <GridWrapper isDarkMode={isDarkMode}>
        <TableWrapper>
          <RetrieveWrapper>
            <Flex direction="row" gap="xs" justify="flex-start" align="flex-center">
              <CustomFilter filterType={routingsHeader} setQuery={setQuery} query={query} />
              <CustomSorter sorterType={routingsHeader} setSort={setSort} sort={sort} />
            </Flex>
            <SearchBox searchType={routingsHeader} setSearch={setSearch} search={search} />
          </RetrieveWrapper>
          <DataGrid
            // onRowClick={(row) => {
            //   openModal(<RoutingDetailForm routingId={row.id} />, null, "");
            // }}
            columns={columns ?? []}
            rows={rows}
            rowHeight={40}
            rowKeyGetter={(row: ItemsGet200ResponseRowsInnerRoutingsInner) => row.id}
            sortColumns={sortColumns}
            selectedRows={selectedRows}
            onSelectedRowsChange={(e) => {
              setSelectedRows(e);
              const newSelectedRows = new Set(selectedRowsRef.current);
              newSelectedRows.add(e);
              selectedRowsRef.current = newSelectedRows;
            }}
          />
        </TableWrapper>
        <PaginationWrapper>
          <Pagination
            onChange={setPage}
            value={activePage}
            total={routing?.data?.totalPages ?? 0}
            size="lg"
            radius="sm"
          />
        </PaginationWrapper>
      </GridWrapper>
    </RoutingTableWrapper>
  );
};

const RoutingTableWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`;

const GridWrapper = styled.div<ColorThemeProps>`
  & *[role="grid"] {
    height: inherit;
    --rdg-background-color: ${(props) => (props.isDarkMode ? "white" : "none")};
    --rdg-header-background-color: ${(props) =>
    props.isDarkMode ? "white" : "none"};
    --rdg-color: ${(props) => (props.isDarkMode ? "black" : "white")};
    --rdg-row-hover-background-color: ${(props) =>
    props.isDarkMode ? "#f5f5f5" : theme?.colors?.gray?.[7]};
  }
  & *[role="columnheader"] {
    // color: #7d8fa9;
    font-size: 12px;
    font-family: Roboto;
    font-weight: 500;
    word-wrap: break-word;
    // border: none;
    box-shadow: none;
    display: flex;
    align-items: center;
  }
  & *[aria-colindex="1"] {
  }

  & *[role="row"] {
    height: 100px;
  }

  & *[role="gridcell"] {
    display: flex;
    align-items: center;
    // border-left: none;
    // border-right: none;
    box-shadow: none;

    & > * {
      justify-content: flex-start;
    }
  }
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  gap: 1rem;
`;
const OptionBox = styled.fieldset`
  display: flex;
  gap: 10px;
  border: none;
`;
const OptionBtn = styled<any>(Button)`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
const RetrieveWrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding-bottom: 10px;
  justify-content: space-between;
`;
